import type { AxiosInstance } from 'axios';
import type { z } from 'zod';

/**
 * Shape every multipart upload accepts.
 *
 * The mobile picker emits `{ uri, type?, name? }`. We keep the
 * mime/name fields optional and fall back to a generic name when the
 * picker doesn't provide one — the server validates the actual content
 * type from the multer-decoded buffer anyway.
 */
export interface UploadablePickerImage {
  /** Local URI (`file://...`) returned by the picker. */
  uri: string;
  /** MIME type from the picker, e.g. `image/png`. */
  type?: string;
  /** Filename as it should arrive at the server. */
  name?: string;
}

/**
 * Web file shape — a plain `File` from an `<input type="file">` or a
 * drag-drop event. Kept opaque to the upload helper; we only check
 * `instanceof Blob` because both `File` and `Blob` extend it.
 */
export type UploadableWebImage = Blob | File;

export type UploadableImage = UploadablePickerImage | UploadableWebImage;

function isWebFile(file: UploadableImage): file is UploadableWebImage {
  // `Blob` is defined on both web and modern React Native runtimes,
  // but the RN picker output is a plain `{ uri, ... }` object — so
  // an `instanceof Blob` check cleanly discriminates the two.
  return typeof Blob !== 'undefined' && file instanceof Blob;
}

function buildFormData(file: UploadableImage): FormData {
  const form = new FormData();
  if (isWebFile(file)) {
    // Browser path. `File` carries name + type; `Blob` doesn't, so we
    // fall back to a generic upload name. The server validates the
    // real mime from the multer-decoded buffer either way.
    //
    // The 3-arg `append(name, value, filename)` overload is in the
    // DOM lib but RN's stricter `FormData` type omits it. This code
    // never executes on RN at runtime (`isWebFile` is gated on
    // `instanceof Blob`, which doesn't apply to RN's `{uri, type, name}`
    // picker payload), but the file is type-checked by both web and
    // mobile tsconfigs. Cast through `Function` to bypass the missing
    // overload.
    const name = 'name' in file && typeof file.name === 'string' ? file.name : 'upload';
    (form.append as (n: string, v: Blob | File, f?: string) => void)('file', file, name);
    return form;
  }
  // React Native path. RN's FormData is patched to accept this shape
  // (the platform-specific blob is appended automatically).
  const fileEntry = {
    uri: file.uri,
    type: file.type ?? 'image/jpeg',
    name: file.name ?? 'upload.jpg',
  };
  form.append('file', fileEntry as unknown as Blob);
  return form;
}

/**
 * PUT a single image to a multipart endpoint and parse the response
 * with the supplied Zod schema. Used by both the org logo and user
 * avatar hooks; the only thing that varies is the URL and the
 * response shape.
 */
export async function putImage<S extends z.ZodTypeAny>(
  http: AxiosInstance,
  url: string,
  file: UploadableImage,
  schema: S,
): Promise<z.output<S>> {
  const form = buildFormData(file);
  const response = await http.put(url, form, {
    // Let axios pick up the `multipart/form-data; boundary=...` header
    // by deleting any caller-provided JSON content-type. RN's fetch
    // requires this so the runtime can stamp its own boundary.
    transformRequest: (data) => data,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return schema.parse(response.data) as z.output<S>;
}
